import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import "./Payment.css";
import { useAuth } from "../../../../context/LoginContext/AuthContext";
import toast from "react-hot-toast";
import { loadRazorpay } from "./LoadRazorpay";
import { PaymentDetailsLeft } from "./PaymentDetailsLeft";
import { PaymentDetailsRightUpdate } from "./PaymentDetailsRightUpdate";
import BackButton from "../../Goback/GoBack";

export const ParentPaymentUpdate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showExitModal, setShowExitModal] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const { user, fetchWithAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [lastFetchedPlans, setLastFetchedPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Sync studentIds from navigation state to localStorage if present
  const stateStudentIds = location.state?.studentIds;
  if (stateStudentIds && stateStudentIds !== localStorage.getItem("student_ids")) {
    localStorage.setItem("student_ids", stateStudentIds);
    localStorage.setItem(
      "number_of_kids",
      String(stateStudentIds.split(",").filter(Boolean).length),
    );
  }

  const rawStudentIds =
    stateStudentIds || localStorage.getItem("student_ids") || "";
  const student_ids = useMemo(
    () =>
      rawStudentIds
        .split(",")
        .map((id) => Number(id.trim()))
        .filter((id) => !isNaN(id) && id > 0),
    [rawStudentIds],
  );

  const number_of_kids =
    localStorage.getItem("number_of_kids") ||
    String(student_ids.length > 0 ? student_ids.length : 1);

  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState("");
  const [estimatedData, setEstimatedData] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [processingGateway, setProcessingGateway] = useState(null);
  const isPaymentProcessing = Boolean(processingGateway);
  const setIsPaymentProcessing = (val) => {
    if (!val) setProcessingGateway(null);
  };

  const getSubscriptionsPayload = useCallback(() => {
    const raw =
      location.state?.studentIds ||
      localStorage.getItem("student_ids") ||
      "";
    return raw
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id && !isNaN(Number(id)) && Number(id) > 0)
      .map((id) => ({ student_id: id }));
  }, [location.state?.studentIds]);

  const handleApply = async () => {
    const trimmed = coupon.trim();
    if (trimmed === "") return;

    setLoading(true);
    setError("");
    if (!user || !user.token) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${apiUrl}/subscriptions/billing/estimate`,
        {
          method: "POST",
          body: JSON.stringify({
            coupon_code: trimmed,
            quantity: number_of_kids,
            plan_id: plans?.plans?.[0]?.id,
            mode: "update",
            student_ids,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setAppliedCoupon(trimmed);
        setCouponSuccess("Coupon Applied Successfully");
        setEstimatedData(data);
        setIsCouponApplied(true);
      } else {
        setError(data?.message || "Invalid coupon code");
      }
    } catch (err) {
      setError("Error validating coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setAppliedCoupon(null);
    setCoupon("");
    setError("");
    setIsCouponApplied(false);
    setCouponSuccess("");
    RemoveCoupon();
  };
  const RemoveCoupon = async () => {
    try {
      const estimateResponse = await fetchWithAuth(
        `${apiUrl}/subscriptions/billing/estimate`,
        {
          method: "POST",
          body: JSON.stringify({
            coupon_code: "",
            quantity: number_of_kids,
            plan_id: plans?.plans?.[0]?.id,
            mode: "update",
            student_ids,
          }),
        },
      );

      const estimateData = await estimateResponse.json();

      if (estimateResponse.ok) {
        setEstimatedData(estimateData);
        setLoadingPlans(false);
      }
    } catch (err) {
      // console.error("Coupon validation error:", err);
      // setError("Error validating coupon");
    }
  };
  useEffect(() => {
    const fetchPlans = async () => {
      if (!user || !user.token) {
        return;
      }

      try {
        const response = await fetchWithAuth(`${apiUrl}/plans`, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch plans: ${response.statusText}`);
        }

        const data = await response.json();

        if (JSON.stringify(data) !== JSON.stringify(lastFetchedPlans)) {
          setPlans(data);
          setLastFetchedPlans(data);
          setLoadingPlans(false);
          localStorage.setItem("trial_days", data?.plans?.[0]?.trial_days);
          setLoadingPlans(true);
          setError("");

          try {
            const estimateResponse = await fetchWithAuth(
              `${apiUrl}/subscriptions/billing/estimate`,
              {
                method: "POST",
                body: JSON.stringify({
                  coupon_code: "",
                  quantity: number_of_kids,
                  plan_id: data?.plans?.[0]?.id,
                  mode: "update",
                  student_ids,
                }),
              },
            );

            const estimateData = await estimateResponse.json();

            if (estimateResponse.ok) {
              setEstimatedData(estimateData);
              setLoadingPlans(false);
            }
          } catch (err) {
            // console.error("Coupon validation error:", err);
            // setError("Error validating coupon");
          }
        }
      } catch (error) {
        toast.error("Error fetching plans:", error.message);
      }
    };

    fetchPlans();
  }, [
    lastFetchedPlans,
    apiUrl,
    user,
    number_of_kids,
    student_ids,
    fetchWithAuth,
  ]);

  // Fire add_to_cart event when plans successfully load
  useEffect(() => {
    if (plans?.plans?.length > 0 && !loadingPlans) {
      const items = plans.plans.map((plan) => ({
        item_id: String(plan.id),
        item_name: plan.name,
        item_category: "YQ Yearly Subscription Plan",
        price: Number(plan.final_price) || 0,
        currency: String(plan.currency || "INR"),
        trial_days: plan.trial_days,
        billing_cycle: plan.billing_cycle,
        tiers: plan.tiers,
        discount_percent: plan.discount_percent,
        description: plan.description,
      }));

      // Push to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
        currency: items[0].currency,
        value: items.reduce((sum, i) => sum + i.price, 0), // sum of all plan prices
        items,
      });

      // Direct GA4 send (for DebugView)
      if (typeof window.gtag === "function") {
        window.gtag("event", "add_to_cart", {
          currency: items[0].currency,
          value: items.reduce((sum, i) => sum + i.price, 0),
          items,
          debug_mode: true, // 👈 ensures you see it in DebugView
        });
        console.log("✅ Sent add_to_cart event to GA4:", {
          currency: items[0].currency,
          value: items.reduce((sum, i) => sum + i.price, 0),
          items,
        });
      }
    }
  }, [plans, loadingPlans]);

  // GA4 begin_checkout event
  const fireBeginCheckout = (plans, coupon, number_of_kids) => {
    if (!plans?.plans?.length) return;

    const items = plans.plans.map((plan) => ({
      item_id: String(plan.id),
      item_name: plan.name,
      item_category: "YQ Yearly Subscription Plan",
      price: Number(plan.final_price) || 0,
      currency: String(plan.currency || "INR"),
      quantity: Number(number_of_kids) || 1,
      coupon: coupon || undefined,
    }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "begin_checkout",
      currency: items[0].currency,
      value: items.reduce((sum, i) => sum + i.price, 0),
      coupon: coupon || undefined,
      items,
    });

    if (typeof window.gtag === "function") {
      window.gtag("event", "begin_checkout", {
        currency: items[0].currency,
        value: items.reduce((sum, i) => sum + i.price, 0),
        coupon: coupon || undefined,
        items,
        debug_mode: true, // so you can see it in DebugView
      });
      console.log("✅ Sent begin_checkout event to GA4:", {
        currency: items[0].currency,
        value: items.reduce((sum, i) => sum + i.price, 0),
        coupon,
        items,
      });
    }
  };

  // GA4 purchase event
  const firePurchase = (
    plans,
    coupon,
    number_of_kids,
    transactionId,
    paymentMethod,
  ) => {
    if (!plans?.plans?.length) return;

    const items = plans.plans.map((plan) => ({
      item_id: String(plan.id),
      item_name: plan.name,
      item_category: "YQ Yearly Subscription Plan",
      price: Number(plan.final_price) || 0,
      currency: String(plan.currency || "INR"),
      quantity: Number(number_of_kids) || 1,
      coupon: coupon || undefined,
    }));

    const value = items.reduce((sum, i) => sum + i.price, 0);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      transaction_id: transactionId || String(Date.now()), // unique order/payment ID
      affiliation: paymentMethod || "Online",
      currency: items[0].currency,
      value,
      coupon: coupon || undefined,
      items,
    });

    if (typeof window.gtag === "function") {
      window.gtag("event", "purchase", {
        transaction_id: transactionId || String(Date.now()),
        affiliation: paymentMethod || "Online",
        currency: items[0].currency,
        value,
        coupon: coupon || undefined,
        items,
        debug_mode: true, // for DebugView
      });
      console.log("✅ Sent purchase event to GA4:", {
        transaction_id: transactionId,
        affiliation: paymentMethod,
        currency: items[0].currency,
        value,
        coupon,
        items,
      });
    }
  };

  const subscribeToPlanStripe = async () => {
    if (processingGateway) return;
    setProcessingGateway("stripe");

    try {
      fireBeginCheckout(plans, coupon, number_of_kids); // 👈 fire event first
      const subscriptions = getSubscriptionsPayload();
      if (subscriptions.length === 0) {
        toast.error("No valid students found for subscription.");
        setIsPaymentProcessing(false);
        return;
      }

      const bodyData = {
        plan_id: String(plans?.plans?.[0]?.id),
        subscriptions,
        mode: "update",
        payment_method: "stripe",
        num_kids: String(subscriptions.length),
      };
      localStorage.setItem("payment_mode", "stripe");
      localStorage.setItem("mode", "update");

      // Only include coupon_code if it's non-empty
      if (coupon.trim()) {
        bodyData.coupon_code = coupon.trim();
      }

      const response = await fetchWithAuth(
        `${apiUrl}/stripe/update-subscription`,
        {
          method: "POST",
          body: JSON.stringify(bodyData),
        },
      );
      const result = await response.json();

      if (!response.ok) {
        toast.error(result.message || result.error);
        setIsPaymentProcessing(false);
        return;
      }

      if (response.ok) {
        localStorage.setItem("mode", "update");
        // Fire purchase event
        firePurchase(
          plans,
          coupon,
          number_of_kids,
          result.subscription_id,
          "Stripe",
        );
        window.location.href = result.approval_link;
      }
      // Handle success (e.g. redirect to Stripe checkout or show confirmation)
    } catch (error) {
      setIsPaymentProcessing(false);
      toast.error("An error occurred during Stripe checkout.");
    }
  };
  const subscribeToPlanPaypal = async () => {
    if (processingGateway) return;
    setProcessingGateway("paypal");

    try {
      fireBeginCheckout(plans, coupon, number_of_kids); // 👈 fire event first
      const subscriptions = getSubscriptionsPayload();
      if (subscriptions.length === 0) {
        toast.error("No valid students found for subscription.");
        setIsPaymentProcessing(false);
        return;
      }

      const bodyData = {
        plan_id: String(plans?.plans?.[0]?.id),
        subscriptions,
        mode: "update",
        payment_method: "paypal",
        num_kids: String(subscriptions.length),
      };
      localStorage.setItem("payment_mode", "paypal");
      localStorage.setItem("mode", "update");

      // Only include coupon_code if it's non-empty
      if (coupon.trim()) {
        bodyData.coupon_code = coupon.trim();
      }

      const response = await fetchWithAuth(
        `${apiUrl}/paypal/update-subscription`,
        {
          method: "POST",
          body: JSON.stringify(bodyData),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.message || result.error);
        setIsPaymentProcessing(false);
        return;
      }

      if (response.ok) {
        localStorage.setItem("mode", "update");
        // Fire purchase event
        firePurchase(
          plans,
          coupon,
          number_of_kids,
          result.subscription_id,
          "PayPal",
        );
        window.location.href = result.approval_link;
      }
    } catch (error) {
      setIsPaymentProcessing(false);
      toast.error("An error occurred during PayPal checkout.");
    }
  };
  const subscribeToPlanRazorPay = async () => {
    if (processingGateway) return;
    setProcessingGateway("razorpay");

    try {
      fireBeginCheckout(plans, coupon, number_of_kids); // 👈 fire event first
      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        toast.error("Razorpay SDK failed to load. Please check your connection.");
        setIsPaymentProcessing(false);
        return;
      }

      const subscriptions = getSubscriptionsPayload();
      if (subscriptions.length === 0) {
        toast.error("No valid students found for subscription.");
        setIsPaymentProcessing(false);
        return;
      }

      const bodyData = {
        plan_id: String(plans?.plans?.[0]?.id),
        subscriptions,
        mode: "update",
        payment_method: "razorpay",
        num_kids: String(subscriptions.length),
      };

      if (coupon.trim()) {
        bodyData.coupon_code = coupon.trim();
      }

      const response = await fetchWithAuth(
        `${apiUrl}/razorpay/update-subscription`,
        {
          method: "POST",
          body: JSON.stringify(bodyData),
        },
      );

      const result = await response.json();
      localStorage.setItem("payment_mode", "razorpay");
      localStorage.setItem("mode", "update");

      if (!response.ok) {
        toast.error(result.message || result.error);
        setIsPaymentProcessing(false);
        return;
      }

      if (!result.subscription_id) {
        toast.error("Failed to update subscription");
        setIsPaymentProcessing(false);
        return;
      }

      localStorage.setItem("mode", "update");

      // ✅ Now window.Razorpay is safe to use
      const options = {
        key: process.env.REACT_APP_PUBLIC_RAZORPAY_TEST_KEY_ID,
        subscription_id: result.subscription_id,
        name: "Yuva Quest",
        description: "Subscription Payment",
        image:
          "https://d2lvj32mp5x83n.cloudfront.net/static/logo-YQ/YQLogoPlain.png",
        theme: { color: "#2d8cf0" },
        handler: async function (resp) {
          // console.log("Payment Success:", resp);
          setTimeout(async () => {
            await fetchWithAuth(`${apiUrl}/razorpay/payment-callback`, {
              method: "POST",
              body: JSON.stringify({
                subscription_id: result?.subscription_id,
              }),
            });

            // Fire purchase event
            firePurchase(
              plans,
              coupon,
              number_of_kids,
              result?.subscription_id,
              "Razorpay",
            );

            toast.success(
              "Subscription registered, waiting for Razorpay to finalize billing details.",
            );
            window.location.href = `/razorpay/result/?subscription_id=${result.subscription_id}&status=success`;
          }, 1000);
        },
        modal: {
          ondismiss: function () {
            setIsPaymentProcessing(false);
            window.location.href = `/razorpay/result/?subscription_id=${result.subscription_id}&status=cancel`;
          },
        },
        prefill: {
          name: user?.name || "Guest User",
          email: user?.email || "test@example.com",
          contact: user?.phone || "9999999999",
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (resp) {
        setIsPaymentProcessing(false);
        window.location.href = `/razorpay/result/?subscription_id=${result.subscription_id}&status=failed`;
      });

      rzp.open();
    } catch (error) {
      setIsPaymentProcessing(false);
      window.location.href = `/razorpay/result/?status=failed`;
    }
  };
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!user || !user.token) {
        return;
      }

      try {
        const response = await fetchWithAuth(`${apiUrl}/parent/subscription`, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch subscription: ${response.statusText}`,
          );
        }

        const data = await response.json();

        setSubscriptionDetails(data);
      } catch (error) {}
    };

    fetchSubscriptionData();
  }, [apiUrl, user, fetchWithAuth]);

  const fallbackPath = window.location.pathname.startsWith("/school-parent")
    ? "/school-parent/dashboard"
    : "/parent/dashboard";

  const handleBackClick = () => {
    setShowExitModal(true);
  };

  const handleConfirmExit = () => {
    setShowExitModal(false);
    navigate(fallbackPath, {
      state: {
        fromPaymentExit: true,
        pendingCount: student_ids.length,
      },
    });
  };

  return (
    <>
      <div className="container mt-4 mb-2">
        <BackButton fallbackPath={fallbackPath} onBack={handleBackClick} />
      </div>
      <div className="container responsive-margin mt-0">
        <div
          className="row align-items-center justify-content-center"
          style={{ gap: "30px" }}
        >
          {/* Left Side */}
          <PaymentDetailsLeft
            loadingPlans={loadingPlans}
            estimatedData={estimatedData}
            plans={plans}
            user={user}
            number_of_kids={number_of_kids}
          />

          {/* Right Side */}
          <PaymentDetailsRightUpdate
            loadingPlans={loadingPlans}
            isCouponApplied={isCouponApplied}
            plans={plans}
            estimatedData={estimatedData}
            appliedCoupon={appliedCoupon}
            coupon={coupon}
            couponSuccess={couponSuccess}
            handleApply={handleApply}
            handleRemove={handleRemove}
            setCoupon={setCoupon}
            loading={loading}
            isPaymentProcessing={isPaymentProcessing}
            processingGateway={processingGateway}
            subscribeToPlanPaypal={subscribeToPlanPaypal}
            subscribeToPlanRazorPay={subscribeToPlanRazorPay}
            subscribeToPlanStripe={subscribeToPlanStripe}
            error={error}
            subscriptionDetails={subscriptionDetails}
          />
        </div>
      </div>

      {/* Leave Payment Confirmation Modal */}
      <Modal
        show={showExitModal}
        onHide={() => setShowExitModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">Leave Payment?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-start gap-3">
            <div className="p-1 text-success fs-3">
              <i className="fa-solid fa-circle-check"></i>
            </div>
            <div>
              <p className="mb-2 fw-semibold text-dark">
                Your kid's profile is already saved!
              </p>
              <p className="text-muted small mb-0">
                You can complete payment now to unlock quests and festivals immediately, or return to your dashboard and pay anytime using the <strong>Pay Now</strong> button on your kid's card.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            className="rounded-pill px-3"
            onClick={handleConfirmExit}
          >
            Go to Dashboard (Pay Later)
          </Button>
          <Button
            variant="primary"
            className="rounded-pill px-3"
            onClick={() => setShowExitModal(false)}
          >
            Continue Payment
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
